import { createFileRoute, Navigate } from '@tanstack/react-router';
import {
  AlertOctagon,
  Bell,
  CalendarClock,
  CheckCircle2,
  Eye,
  FolderKanban,
  Inbox,
  LayoutDashboard,
  Mail,
  Pencil,
  Search,
  Settings,
  ShieldCheck,
  Trash2,
  Users,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

import { ActionMenu } from '@/shared/ui/action-menu';
import { AppShell } from '@/shared/ui/app-shell';
import { AuthFooter } from '@/shared/ui/auth-footer';
import { AuthTopBar } from '@/shared/ui/auth-topbar';
import { Breadcrumbs } from '@/shared/ui/breadcrumbs';
import {
  ConfirmActionDialog,
  type ConfirmActionDialogVariant,
} from '@/shared/ui/confirm-action-dialog';
import { DataTable } from '@/shared/ui/data-table';
import { EmptyState } from '@/shared/ui/empty-state';
import { FilterBar, type FilterChip } from '@/shared/ui/filter-bar';
import { FormActions } from '@/shared/ui/form-actions';
import { FormField } from '@/shared/ui/form-field';
import { FormSection } from '@/shared/ui/form-section';
import { IconBadge, type IconBadgeProps } from '@/shared/ui/icon-badge';
import { InlineFeedback, type InlineFeedbackVariant } from '@/shared/ui/inline-feedback';
import { LogoAni } from '@/shared/ui/logo-ani';
import { PageHeader } from '@/shared/ui/page-header';
import { PasswordInput } from '@/shared/ui/password-input';
import { PasswordStrengthMeter, type PasswordStrength } from '@/shared/ui/password-strength-meter';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/ui/shadcn/card';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import { Sidebar } from '@/shared/ui/sidebar';
import { StatCard } from '@/shared/ui/stat-card';
import { StatusBadge, type StatusTone } from '@/shared/ui/status-badge';
import { ThemeToggle } from '@/shared/ui/theme';
import { TopBar } from '@/shared/ui/topbar';

import type { ColumnDef, SortingState } from '@tanstack/react-table';

/**
 * Demo de primitivas atómicas. Solo accesible en dev. En producción
 * redirige a `/`. Se mantiene fuera del flujo de usuario.
 */
export const Route = createFileRoute('/__dev/primitives')({
  component: PrimitivesPage,
});

const BUTTON_VARIANTS = [
  'default',
  'destructive',
  'outline',
  'secondary',
  'ghost',
  'link',
] as const;
const BUTTON_SIZES = ['sm', 'default', 'lg', 'xl'] as const;
const CARD_VARIANTS = ['default', 'auth', 'flat', 'elevated'] as const;
const ICON_BADGE_VARIANTS: NonNullable<IconBadgeProps['variant']>[] = [
  'primary',
  'success',
  'warning',
  'danger',
  'info',
  'neutral',
];
const INLINE_FEEDBACK_VARIANTS: InlineFeedbackVariant[] = ['success', 'warning', 'danger', 'info'];
const STRENGTH_DEMOS: { strength: PasswordStrength; label: string }[] = [
  { strength: 'weak', label: 'Débil (1 criterio)' },
  { strength: 'medium', label: 'Media (2 criterios)' },
  { strength: 'strong', label: 'Fuerte (todos)' },
];

function PrimitivesPage() {
  if (!import.meta.env.DEV) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-12 p-8">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <LogoAni className="h-12" />
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Sub-fase 2.1
            </p>
            <h1 className="text-3xl font-semibold tracking-tight">Primitivas atómicas</h1>
          </div>
        </div>
        <ThemeToggle />
      </header>

      <DemoSection title="Button — variantes × tamaños">
        {BUTTON_VARIANTS.map((variant) => (
          <div key={variant} className="space-y-2">
            <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
              {variant}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {BUTTON_SIZES.map((size) => (
                <Button key={size} variant={variant} size={size}>
                  {variant} {size}
                </Button>
              ))}
              <Button variant={variant} size="icon" aria-label="icon">
                <Trash2 />
              </Button>
              <Button variant={variant} disabled>
                disabled
              </Button>
            </div>
          </div>
        ))}
      </DemoSection>

      <DemoSection title="Input — variantes">
        <div className="grid max-w-2xl gap-4">
          <Field label="Default">
            <Input placeholder="Texto libre" />
          </Field>
          <Field label="Con leadingIcon">
            <Input leadingIcon={<Mail />} placeholder="correo@dominio.com" />
          </Field>
          <Field label="Con trailingIcon">
            <Input trailingIcon={<Search />} placeholder="Buscar..." />
          </Field>
          <Field label="Ambos">
            <Input leadingIcon={<Mail />} trailingIcon={<Search />} placeholder="..." />
          </Field>
          <Field label="Disabled">
            <Input disabled placeholder="No editable" defaultValue="bloqueado" />
          </Field>
          <Field label="Estado inválido (aria-invalid)">
            <Input aria-invalid="true" placeholder="con error" />
          </Field>
        </div>
      </DemoSection>

      <DemoSection title="PasswordInput">
        <div className="grid max-w-2xl gap-4">
          <Field label="Vacío">
            <PasswordInput placeholder="Contraseña" />
          </Field>
          <Field label="Con valor inicial">
            <PasswordInput placeholder="Contraseña" defaultValue="secreto123" />
          </Field>
          <Field label="Disabled">
            <PasswordInput disabled placeholder="bloqueado" defaultValue="secret" />
          </Field>
        </div>
      </DemoSection>

      <DemoSection title="Card — variantes">
        <div className="grid gap-6 md:grid-cols-2">
          {CARD_VARIANTS.map((variant) => (
            <Card key={variant} variant={variant}>
              <CardHeader>
                <CardTitle>Variant: {variant}</CardTitle>
                <CardDescription>
                  Token-driven. Rounded y shadow vienen de --card-* en :root/.dark.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Contenido de ejemplo para validar padding y estructura.
                </p>
              </CardContent>
              <CardFooter>
                <Button variant="outline" size="sm">
                  Acción
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </DemoSection>

      <DemoSection title="Composites de feedback">
        <div className="space-y-2">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            IconBadge — variantes
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {ICON_BADGE_VARIANTS.map((variant) => (
              <IconBadge key={variant} variant={variant} icon={<ShieldCheck />} />
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            IconBadge — tamaños (variant=primary)
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <IconBadge variant="primary" size="sm" icon={<ShieldCheck />} />
            <IconBadge variant="primary" size="md" icon={<ShieldCheck />} />
            <IconBadge variant="primary" size="lg" icon={<ShieldCheck />} />
          </div>
        </div>

        <div className="space-y-2">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            InlineFeedback
          </p>
          <div className="flex flex-col gap-2">
            {INLINE_FEEDBACK_VARIANTS.map((variant) => (
              <InlineFeedback key={variant} variant={variant}>
                Mensaje {variant}
              </InlineFeedback>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            EmptyState
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <Card variant="flat">
              <CardContent className="pt-6">
                <EmptyState
                  icon={<Inbox />}
                  title="Sin resultados"
                  description="Ajuste los filtros para ver más datos."
                />
              </CardContent>
            </Card>
            <Card variant="flat">
              <CardContent className="pt-6">
                <EmptyState
                  icon={<Users />}
                  iconVariant="primary"
                  title="Sin supervisores asignados"
                  description="Vincule usuarios al proyecto para empezar."
                  action={<Button size="sm">Agregar supervisor</Button>}
                />
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="space-y-3">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            PasswordStrengthMeter
          </p>
          <div className="grid max-w-2xl gap-6">
            {STRENGTH_DEMOS.map(({ strength, label }, idx) => (
              <div key={strength} className="space-y-1.5">
                <Label className="text-muted-foreground">{label}</Label>
                <PasswordStrengthMeter
                  strength={strength}
                  criteria={[
                    { label: 'Mínimo 8 caracteres', met: idx >= 0 },
                    { label: 'Una mayúscula', met: idx >= 1 },
                    { label: 'Un número', met: idx >= 2 },
                    { label: 'Un símbolo', met: idx >= 2 },
                  ]}
                />
              </div>
            ))}
          </div>
        </div>
      </DemoSection>

      <DemoSection title="Composites de formulario">
        <div className="space-y-2">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            FormField — variantes
          </p>
          <div className="grid max-w-2xl gap-4">
            <FormField label="Nombre" htmlFor="demo-name">
              <Input id="demo-name" placeholder="Ana López" />
            </FormField>
            <FormField
              label="Correo institucional"
              htmlFor="demo-email"
              description="Use el correo asignado por la institución."
            >
              <Input id="demo-email" type="email" placeholder="usuario@ani.gob.gt" />
            </FormField>
            <FormField
              label="Teléfono"
              htmlFor="demo-phone"
              error="Formato inválido. Use 8 dígitos sin espacios."
              description="Solo Guatemala (+502)."
            >
              <Input id="demo-phone" aria-invalid="true" defaultValue="5556" />
            </FormField>
            <FormField
              label="Contraseña"
              htmlFor="demo-pass"
              required
              description="Mínimo 12 caracteres con mayúsculas, números y símbolos."
            >
              <PasswordInput id="demo-pass" />
            </FormField>
          </div>
        </div>

        <div className="space-y-2">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            FormSection — con y sin header
          </p>
          <div className="grid max-w-2xl gap-8">
            <FormSection>
              <FormField label="Campo solo" htmlFor="demo-solo">
                <Input id="demo-solo" placeholder="Sin agrupación visible" />
              </FormField>
            </FormSection>
            <FormSection
              title="Datos de contacto"
              description="Información que usaremos para notificarle."
            >
              <FormField label="Correo" htmlFor="demo-sec-email">
                <Input id="demo-sec-email" type="email" />
              </FormField>
              <FormField label="Teléfono" htmlFor="demo-sec-phone">
                <Input id="demo-sec-phone" />
              </FormField>
            </FormSection>
          </div>
        </div>

        <div className="space-y-2">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            FormActions — 3 alineaciones
          </p>
          <div className="grid max-w-2xl gap-6">
            <FormActions align="right">
              <Button variant="outline">Cancelar</Button>
              <Button>Guardar</Button>
            </FormActions>
            <FormActions align="between">
              <Button variant="destructive">Eliminar</Button>
              <Button>Confirmar</Button>
            </FormActions>
            <FormActions align="left">
              <Button variant="secondary">Guardar borrador</Button>
            </FormActions>
          </div>
        </div>

        <ConfirmActionDialogDemo />
      </DemoSection>

      <DemoSection title="Composites de listado">
        <ListingComposites />
      </DemoSection>

      <DemoSection title="App Chrome">
        <AppChromeDemo />
      </DemoSection>

      <DemoSection title="Auth Chrome">
        <AuthChromeDemo />
      </DemoSection>
    </div>
  );
}

function ConfirmActionDialogDemo() {
  const [openVariant, setOpenVariant] = useState<ConfirmActionDialogVariant | null>(null);

  return (
    <div className="space-y-2">
      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
        ConfirmActionDialog — 3 variantes
      </p>
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" onClick={() => setOpenVariant('info')}>
          Cerrar sesión (info)
        </Button>
        <Button variant="outline" onClick={() => setOpenVariant('warning')}>
          Desactivar usuario (warning)
        </Button>
        <Button variant="outline" onClick={() => setOpenVariant('destructive')}>
          Eliminar proyecto (destructive + confirmWord)
        </Button>
      </div>

      <ConfirmActionDialog
        open={openVariant === 'info'}
        onOpenChange={(o) => !o && setOpenVariant(null)}
        title="¿Cerrar sesión?"
        description="Tendrá que volver a autenticarse para usar la plataforma."
        variant="info"
        confirmLabel="Cerrar sesión"
        onConfirm={() => setOpenVariant(null)}
      />
      <ConfirmActionDialog
        open={openVariant === 'warning'}
        onOpenChange={(o) => !o && setOpenVariant(null)}
        title="¿Desactivar usuario?"
        description="El usuario no podrá iniciar sesión hasta que se reactive."
        variant="warning"
        confirmLabel="Desactivar"
        onConfirm={() => setOpenVariant(null)}
      />
      <ConfirmActionDialog
        open={openVariant === 'destructive'}
        onOpenChange={(o) => !o && setOpenVariant(null)}
        title="¿Eliminar proyecto?"
        description="Esta acción es irreversible. Todas las incidencias y archivos del proyecto se perderán."
        variant="destructive"
        confirmLabel="Eliminar"
        confirmWord="ELIMINAR"
        onConfirm={() => setOpenVariant(null)}
      />
    </div>
  );
}

function AppChromeDemo() {
  const sidebarLogo = (
    <div className="flex size-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
      <ShieldCheck aria-hidden="true" className="size-5" />
    </div>
  );

  const sidebar = (
    <Sidebar
      logo={sidebarLogo}
      groups={[
        {
          id: 'main',
          label: 'Principal',
          items: [
            {
              id: 'dashboard',
              label: 'Dashboard',
              icon: <LayoutDashboard />,
              isActive: true,
              onClick: () => {},
            },
            { id: 'users', label: 'Usuarios', icon: <Users />, badge: 3, onClick: () => {} },
            { id: 'inbox', label: 'Bandeja', icon: <Inbox />, onClick: () => {} },
            { id: 'cfg', label: 'Configuración', icon: <Settings />, onClick: () => {} },
          ],
        },
      ]}
    />
  );

  const topbar = (
    <TopBar
      searchSlot={
        <Input leadingIcon={<Search />} placeholder="Buscar…" aria-label="Búsqueda global" />
      }
      rightSlot={
        <>
          <Button variant="ghost" size="icon" aria-label="Notificaciones">
            <Bell />
          </Button>
          <ThemeToggle />
        </>
      }
    />
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          AppShell miniaturizado (sidebar + topbar + contenido)
        </p>
        <div className="min-h-[400px] overflow-hidden rounded-[var(--card-radius)] border border-border">
          <AppShell sidebar={sidebar} topbar={topbar}>
            <PageHeader
              title="Resumen general"
              description="Métricas operativas y atajos a las áreas de trabajo."
              breadcrumbs={
                <Breadcrumbs
                  items={[
                    { id: '1', label: 'Inicio', href: '/' },
                    { id: '2', label: 'Resumen' },
                  ]}
                />
              }
              actions={
                <>
                  <Button variant="outline" size="sm">
                    Exportar
                  </Button>
                  <Button size="sm">Nueva incidencia</Button>
                </>
              }
            />
            <p className="mt-4 text-sm text-muted-foreground">
              El contenido scrollable de la pantalla iría acá. La sidebar queda fija a la izquierda
              y la topbar sticky arriba.
            </p>
          </AppShell>
        </div>
      </div>

      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          PageHeader con breadcrumbs + título + descripción + actions
        </p>
        <PageHeader
          title="Usuarios"
          description="Administración de cuentas, roles y permisos del sistema."
          breadcrumbs={
            <Breadcrumbs
              items={[
                { id: '1', label: 'Inicio', href: '/' },
                { id: '2', label: 'Administración', href: '/admin' },
                { id: '3', label: 'Usuarios' },
              ]}
            />
          }
          actions={
            <>
              <Button variant="outline" size="sm">
                Importar CSV
              </Button>
              <Button size="sm">Crear usuario</Button>
            </>
          }
        />
      </div>

      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          Breadcrumbs aislados (3 items, el último activo)
        </p>
        <Breadcrumbs
          items={[
            { id: '1', label: 'Inicio', href: '/' },
            { id: '2', label: 'Proyectos', href: '/proyectos' },
            { id: '3', label: 'Carretera CA-1' },
          ]}
        />
      </div>
    </div>
  );
}

function AuthChromeDemo() {
  const logo = (
    <div className="flex items-center gap-2">
      <div className="flex size-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
        <ShieldCheck aria-hidden="true" className="size-5" />
      </div>
      <span className="text-sm font-semibold">ANI</span>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          AuthTopBar — transparente con logo + botón
        </p>
        <div className="overflow-hidden rounded-[var(--card-radius)] border border-border bg-[var(--background-app-auth)]">
          <AuthTopBar logo={logo} rightSlot={<Button variant="outline">Iniciar sesión</Button>} />
        </div>
      </div>

      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          AuthFooter — copyright + links
        </p>
        <div className="overflow-hidden rounded-[var(--card-radius)] border border-border bg-[var(--background-app-auth)]">
          <AuthFooter
            copyright="© 2026 Agencia Nacional de Infraestructura"
            links={[
              { id: 'priv', label: 'Política de privacidad', href: '/privacidad' },
              { id: 'terms', label: 'Términos', href: '/terminos' },
            ]}
          />
        </div>
      </div>

      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          Mini-layout completo de auth (topbar + card centrada + footer)
        </p>
        <div className="flex min-h-[600px] flex-col overflow-hidden rounded-[var(--card-radius)] border border-border bg-[var(--background-app-auth)]">
          <AuthTopBar logo={logo} rightSlot={<Button variant="outline">Crear cuenta</Button>} />
          <main className="flex flex-1 items-center justify-center px-4">
            <Card variant="auth" className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Iniciar sesión</CardTitle>
                <CardDescription>
                  Ingrese sus credenciales institucionales para continuar.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField label="Correo institucional" htmlFor="demo-auth-email">
                  <Input id="demo-auth-email" type="email" placeholder="usuario@ani.gob.gt" />
                </FormField>
                <FormField label="Contraseña" htmlFor="demo-auth-pass">
                  <PasswordInput id="demo-auth-pass" />
                </FormField>
              </CardContent>
              <CardFooter className="flex-col gap-2">
                <Button className="w-full">Iniciar sesión</Button>
              </CardFooter>
            </Card>
          </main>
          <AuthFooter
            copyright="© 2026 Agencia Nacional de Infraestructura"
            links={[{ id: 'priv', label: 'Política de privacidad', href: '/privacidad' }]}
          />
        </div>
      </div>
    </div>
  );
}

function DemoSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      <div className="space-y-6">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

type ProyectoRow = {
  id: number;
  nombre: string;
  estado: { label: string; tone: StatusTone };
  fecha: string;
};

const PROYECTOS_MOCK: ProyectoRow[] = [
  {
    id: 1,
    nombre: 'Autopista CA-1 Tramo Norte',
    estado: { label: 'Activo', tone: 'success' },
    fecha: '2025-09-12',
  },
  {
    id: 2,
    nombre: 'Puente Río Motagua',
    estado: { label: 'En revisión', tone: 'warning' },
    fecha: '2025-10-04',
  },
  {
    id: 3,
    nombre: 'Ampliación Boulevard Liberación',
    estado: { label: 'En proceso', tone: 'info' },
    fecha: '2025-11-01',
  },
  {
    id: 4,
    nombre: 'Carretera CA-2 Occidente',
    estado: { label: 'Suspendido', tone: 'danger' },
    fecha: '2025-08-21',
  },
  {
    id: 5,
    nombre: 'Túnel Volcán Pacaya',
    estado: { label: 'Borrador', tone: 'neutral' },
    fecha: '2026-01-15',
  },
];

const STATUS_TONES_DEMO: { tone: StatusTone; label: string }[] = [
  { tone: 'success', label: 'Activo' },
  { tone: 'warning', label: 'En revisión' },
  { tone: 'danger', label: 'Suspendido' },
  { tone: 'info', label: 'En proceso' },
  { tone: 'neutral', label: 'Borrador' },
  { tone: 'primary', label: 'Cerrado' },
];

function ListingComposites() {
  const [search, setSearch] = useState('');
  const [chips, setChips] = useState<FilterChip[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Inicializar los chips de demo una sola vez con sus handlers.
  useMemo(() => {
    setChips([
      {
        id: 'estado',
        label: 'Estado: Activo',
        onRemove: () => setChips((c) => c.filter((x) => x.id !== 'estado')),
      },
      {
        id: 'tipo',
        label: 'Tipo: Autopista',
        onRemove: () => setChips((c) => c.filter((x) => x.id !== 'tipo')),
      },
      {
        id: 'origen',
        label: 'Origen: Pública',
        onRemove: () => setChips((c) => c.filter((x) => x.id !== 'origen')),
      },
    ]);
  }, []);

  const columns = useMemo<ColumnDef<ProyectoRow, unknown>[]>(
    () => [
      { accessorKey: 'nombre', header: 'Nombre' },
      {
        accessorKey: 'estado',
        header: 'Estado',
        enableSorting: false,
        cell: ({ row }) => (
          <StatusBadge tone={row.original.estado.tone}>{row.original.estado.label}</StatusBadge>
        ),
      },
      { accessorKey: 'fecha', header: 'Fecha' },
      {
        id: 'acciones',
        header: '',
        enableSorting: false,
        cell: () => (
          <ActionMenu
            items={[
              { id: 'ver', label: 'Ver detalles', icon: <Eye />, onSelect: () => {} },
              { id: 'editar', label: 'Editar', icon: <Pencil />, onSelect: () => {} },
              {
                id: 'eliminar',
                label: 'Eliminar',
                icon: <Trash2 />,
                variant: 'destructive',
                onSelect: () => {},
              },
            ]}
          />
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          StatCard — grid de KPIs
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={<FolderKanban />}
            iconVariant="info"
            label="Total de proyectos"
            value={12}
            description="vs. mes anterior"
          />
          <StatCard icon={<CheckCircle2 />} iconVariant="success" label="Activos" value={5} />
          <StatCard icon={<CalendarClock />} iconVariant="warning" label="Suspendidos" value={1} />
          <StatCard
            icon={<AlertOctagon />}
            iconVariant="danger"
            label="Incidencias vencidas"
            value={3}
            description="último mes"
          />
        </div>
      </div>

      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          StatusBadge — 6 tones
        </p>
        <div className="flex flex-wrap gap-2">
          {STATUS_TONES_DEMO.map(({ tone, label }) => (
            <StatusBadge key={tone} tone={tone}>
              {label}
            </StatusBadge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          DataTable — con data
        </p>
        <DataTable
          columns={columns}
          data={PROYECTOS_MOCK}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      </div>

      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          DataTable — isLoading
        </p>
        <DataTable columns={columns} data={[]} isLoading />
      </div>

      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          DataTable — empty state default
        </p>
        <DataTable columns={columns} data={[]} />
      </div>

      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          FilterBar — interactivo
        </p>
        <FilterBar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Buscar proyectos…"
          chips={chips}
          onClearAll={() => setChips([])}
        />
        <p className="text-xs text-muted-foreground">
          Valor debounceado: <span className="font-mono">{search || '—'}</span> · chips activos:{' '}
          <span className="font-mono">{chips.length}</span>
        </p>
      </div>

      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          ActionMenu — trigger default
        </p>
        <ActionMenu
          items={[
            { id: 'ver', label: 'Ver detalles', icon: <Eye />, onSelect: () => {} },
            { id: 'editar', label: 'Editar', icon: <Pencil />, onSelect: () => {} },
            {
              id: 'eliminar',
              label: 'Eliminar',
              icon: <Trash2 />,
              variant: 'destructive',
              onSelect: () => {},
            },
          ]}
        />
      </div>

      <div className="space-y-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          ActionMenu — trigger custom
        </p>
        <ActionMenu
          trigger={<Button variant="outline">Acciones</Button>}
          items={[
            { id: 'ver', label: 'Ver detalles', icon: <Eye />, onSelect: () => {} },
            { id: 'editar', label: 'Editar', icon: <Pencil />, onSelect: () => {} },
          ]}
        />
      </div>
    </>
  );
}
